/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import type { Station, GroupedStation } from '../types';
import type { Station as PurchasesStation } from '@airasia-rn/purchases';
import {
  SNAP_SUPPORTED_PROFILES,
  DESTINATION_LOOKUP_STATION_OVERRIDES,
} from '../configs/constants';

/**
 * Station with priority for sorting
 */
interface StationWithPriority extends Station {
  Priority?: number;
}

/**
 * Pre-normalized station fields for fast matching (computed once at index build time)
 */
interface NormalizedStation {
  index: number;
  station: Station;
  stationName: string;
  stationCode: string;
  pinYin: string;
  alternativeName: string;
  airportName: string;
  countryName: string;
  stationNameWords: string[];
  alternativeNameWords: string[];
  airportNameWords: string[];
  countryNameWords: string[];
}

const MAX_PREFIX_LENGTH = 3;
const LRU_CACHE_MAX = 50;

/**
 * Normalize a station for fast matching (lowercase + word splitting done once)
 */
function normalizeStation(station: Station, index: number): NormalizedStation {
  const stationName = (station.StationName || '').toLowerCase();
  const alternativeName = (station.AlternativeName || '').toLowerCase();
  const airportName = (station.AirportName || '').toLowerCase();
  const countryName = (station.CountryName || '').toLowerCase();

  return {
    index,
    station,
    stationName,
    stationCode: (station.StationCode || '').toLowerCase(),
    pinYin: (station.PinYin || '').toLowerCase(),
    alternativeName,
    airportName,
    countryName,
    stationNameWords: stationName.split(' ').filter(Boolean),
    alternativeNameWords: alternativeName.split(' ').filter(Boolean),
    airportNameWords: airportName.split(' ').filter(Boolean),
    countryNameWords: countryName.split(' ').filter(Boolean),
  };
}

/**
 * Build a prefix index mapping 1-3 char prefixes to candidate station indices.
 * Indexes all searchable fields and their individual words for O(1) candidate lookup.
 */
function buildPrefixIndex(
  normalized: NormalizedStation[],
): Map<string, Set<number>> {
  const index = new Map<string, Set<number>>();

  const addPrefixes = (text: string, stationIdx: number) => {
    if (!text) return;
    const len = Math.min(text.length, MAX_PREFIX_LENGTH);
    for (let i = 1; i <= len; i++) {
      const prefix = text.substring(0, i);
      let set = index.get(prefix);
      if (!set) {
        set = new Set();
        index.set(prefix, set);
      }
      set.add(stationIdx);
    }
  };

  normalized.forEach((ns) => {
    const idx = ns.index;
    // Index full field values
    addPrefixes(ns.stationName, idx);
    addPrefixes(ns.stationCode, idx);
    addPrefixes(ns.pinYin, idx);
    addPrefixes(ns.alternativeName, idx);
    addPrefixes(ns.airportName, idx);
    addPrefixes(ns.countryName, idx);
    // Index individual words
    ns.stationNameWords.forEach((w) => addPrefixes(w, idx));
    ns.alternativeNameWords.forEach((w) => addPrefixes(w, idx));
    ns.airportNameWords.forEach((w) => addPrefixes(w, idx));
    ns.countryNameWords.forEach((w) => addPrefixes(w, idx));
  });

  return index;
}

/**
 * Match priority on pre-normalized data (same 0-9 logic as production, no toLowerCase per call)
 */
function matchPriority(ns: NormalizedStation, input: string): number | null {
  if (!ns.stationName || !ns.stationCode) return null;

  if (ns.stationName.startsWith(input)) return 0;
  if (ns.stationCode.startsWith(input)) return 1;
  if (ns.pinYin.startsWith(input)) return 2;
  if (ns.stationNameWords.some((w) => w.startsWith(input))) return 3;
  if (ns.alternativeName.startsWith(input)) return 4;
  if (ns.alternativeNameWords.some((w) => w.startsWith(input))) return 5;
  if (ns.airportName.startsWith(input)) return 6;
  if (ns.airportNameWords.some((w) => w.startsWith(input))) return 7;
  if (ns.countryName.startsWith(input)) return 8;
  if (ns.countryNameWords.some((w) => w.startsWith(input))) return 9;

  return null;
}

/**
 * Indexed station search with prefix-based candidate narrowing and LRU result cache.
 * ~1000 stations, typing "kul" -> prefix index narrows to ~20 candidates instead of scanning all.
 */
export class StationSearchIndex {
  private normalized: NormalizedStation[];
  private prefixIndex: Map<string, Set<number>>;
  private cache: Map<string, Station[]>;
  private cacheKeys: string[];

  constructor(stations: Station[]) {
    this.normalized = stations.map((s, i) => normalizeStation(s, i));
    this.prefixIndex = buildPrefixIndex(this.normalized);
    this.cache = new Map();
    this.cacheKeys = [];
  }

  search(query: string): Station[] {
    if (!query || query.trim().length === 0) return [];

    const input = query.toLowerCase().trim();

    // Check LRU cache
    const cached = this.cache.get(input);
    if (cached) return cached;

    // Get candidate indices from prefix index
    const prefixKey = input.substring(
      0,
      Math.min(input.length, MAX_PREFIX_LENGTH),
    );
    const candidateIndices = this.prefixIndex.get(prefixKey);

    let results: StationWithPriority[];

    if (candidateIndices) {
      // Only match against candidates from prefix index
      results = [];
      candidateIndices.forEach((idx) => {
        const ns = this.normalized[idx];
        const priority = matchPriority(ns, input);
        if (priority !== null) {
          results.push({ ...ns.station, Priority: priority });
        }
      });
    } else {
      // No prefix match, no results
      results = [];
    }

    // Sort by priority then station name
    results.sort(
      (a, b) =>
        (a.Priority !== undefined ? a.Priority : 999) -
          (b.Priority !== undefined ? b.Priority : 999) ||
        a.StationName.localeCompare(b.StationName),
    );

    // LRU cache eviction
    if (this.cacheKeys.length >= LRU_CACHE_MAX) {
      const evictKey = this.cacheKeys.shift();
      if (evictKey) this.cache.delete(evictKey);
    }
    this.cache.set(input, results);
    this.cacheKeys.push(input);

    return results;
  }
}

/**
 * Checks if station matches user input and assigns priority
 * Matches production logic from @snap-search-widget/AaFilterUtil.js
 * @param station - Station to check
 * @param input - Lowercase search query
 * @returns true if station matches, false otherwise (Priority assigned to station object)
 */
function stationMatchesUserInput(
  station: StationWithPriority,
  input: string,
): boolean {
  if (!station || !station.StationName || !station.StationCode) return false;

  const st = {
    StationName: station.StationName.toLowerCase(),
    StationCode: station.StationCode.toLowerCase(),
    PinYin: (station.PinYin || '').toLowerCase(),
    AlternativeName: (station.AlternativeName || '').toLowerCase(),
    AirportName: (station.AirportName || '').toLowerCase(),
    CountryName: (station.CountryName || '').toLowerCase(),
  };

  // Use switch(true) to stop at first match (highest priority)
  // Matches production exactly - does NOT check CityName
  switch (true) {
    case st.StationName.startsWith(input):
      station.Priority = 0;
      return true;
    case st.StationCode.startsWith(input):
      station.Priority = 1;
      return true;
    case st.PinYin.startsWith(input):
      station.Priority = 2;
      return true;
    case st.StationName.split(' ').some((val) => val.startsWith(input)):
      station.Priority = 3;
      return true;
    case st.AlternativeName.startsWith(input):
      station.Priority = 4;
      return true;
    case st.AlternativeName.split(' ').some((val) => val.startsWith(input)):
      station.Priority = 5;
      return true;
    case st.AirportName.startsWith(input):
      station.Priority = 6;
      return true;
    case st.AirportName.split(' ').some((val) => val.startsWith(input)):
      station.Priority = 7;
      return true;
    case st.CountryName.startsWith(input):
      station.Priority = 8;
      return true;
    case st.CountryName.split(' ').some((val) => val.startsWith(input)):
      station.Priority = 9;
      return true;
    default:
      return false;
  }
}

/**
 * Searches stations by query matching against multiple fields with priority-based ranking
 * Matches production logic from @snap-search-widget
 * @param stations - Array of stations to search
 * @param query - Search query string
 * @returns Filtered and ranked stations
 * @deprecated Use StationSearchIndex for better performance with repeated searches
 */
export function searchStations(stations: Station[], query: string): Station[] {
  if (!query || query.trim().length === 0) {
    return [];
  }

  const normalizedQuery = query.toLowerCase().trim();

  // Create new array with priority for each station
  const stationsWithPriority: StationWithPriority[] = [];

  stations.forEach((station) => {
    const stationCopy = { ...station } as StationWithPriority;
    if (stationMatchesUserInput(stationCopy, normalizedQuery)) {
      stationsWithPriority.push(stationCopy);
    }
  });

  // Sort by priority then station name (matches production sortStations)
  stationsWithPriority.sort(
    (a, b) =>
      (a.Priority !== undefined ? a.Priority : 999) -
        (b.Priority !== undefined ? b.Priority : 999) ||
      a.StationName.localeCompare(b.StationName),
  );

  return stationsWithPriority;
}

/**
 * Groups stations by city for hierarchical display
 */
export function groupStationsByCity(stations: Station[]): GroupedStation[] {
  const cityMap = new Map<string, GroupedStation>();

  stations.forEach((station) => {
    const existing = cityMap.get(station.CityCode);
    if (existing) {
      existing.airports.push(station);
    } else {
      cityMap.set(station.CityCode, {
        cityCode: station.CityCode,
        cityName: station.CityName,
        countryName: station.CountryName,
        airports: [station],
      });
    }
  });

  // Convert to array and sort airports within each city
  return Array.from(cityMap.values()).map((group) => ({
    ...group,
    airports: group.airports.sort((a, b) => b.Weightage - a.Weightage),
  }));
}

/**
 * Resolves which station code the destination-suggestion request should use.
 * Origins listed in DESTINATION_LOOKUP_STATION_OVERRIDES (BKK -> DMK) are
 * remapped when the mapping flag is on; every other origin is returned as is.
 * The user's selected origin is never changed by this — lookup only.
 *
 * @param stationCode - Origin station code selected by the user
 * @param isMappingEnabled - Feature-flag state (snap_bkk_dmk_mapping_enabled)
 * @returns Station code to request destinations for
 */
export function resolveDestinationLookupStationCode(
  stationCode: string,
  isMappingEnabled: boolean,
): string {
  if (!isMappingEnabled || !stationCode) return stationCode;
  return (
    DESTINATION_LOOKUP_STATION_OVERRIDES[stationCode.toUpperCase()] ??
    stationCode
  );
}

/**
 * Filters destinations that support SNAP (dotrez inventory)
 * @param destinations - Map of destinations
 * @returns Filtered destination codes
 */
export function filterDestinationsForSnap(
  destinations: Record<string, { SupportedAirlineProfiles?: string }>,
): string[] {
  return Object.entries(destinations)
    .filter(([_, dest]) => {
      const profiles = dest.SupportedAirlineProfiles?.toLowerCase() || '';
      return SNAP_SUPPORTED_PROFILES.some((profile) =>
        profiles.includes(profile.toLowerCase()),
      );
    })
    .map(([code]) => code);
}

/**
 * Filters stations that support SNAP (dotrez inventory)
 * Uses AAFlight flag from station payloads (present in stations API response)
 */
export function filterStationsForSnap(stations: Station[]): Station[] {
  return stations.filter((station) => Boolean(station.AAFlight));
}

/**
 * The nearest airport SNAP can use as an origin: the first of `codes` (closest
 * first) that is among `stations`, which are the SNAP-eligible ones. An airport
 * SNAP does not fly from is skipped for the next nearest.
 */
export function findNearestSnapOrigin(
  codes: string[],
  stations: Station[],
): Station | undefined {
  for (const code of codes) {
    const station = stations.find(
      (candidate) => candidate.StationCode === code,
    );

    if (station) return station;
  }

  return undefined;
}

export function mapPurchasesStationToSnap(station: PurchasesStation): Station {
  const lat = Number(station.Lat ?? 0);
  const long = Number(station.Long ?? 0);
  const raw = station as PurchasesStation & {
    AAFlight?: boolean;
    Dest?: string;
    Tag?: string;
    Weightage?: number | string;
  };
  const weightage = Number(raw.Weightage ?? 0);

  return {
    Lat: Number.isFinite(lat) ? lat : 0,
    Long: Number.isFinite(long) ? long : 0,
    Dest: raw.Dest ?? '',
    PinYin: station.PinYin ?? '',
    AAFlight: Boolean(raw.AAFlight),
    CityCode: station.CityCode?.trim().toUpperCase() ?? '',
    CityName: station.CityName ?? '',
    TimeZone: station.TimeZone ?? '',
    AirportName: station.AirportName ?? '',
    CountryCode: station.CountryCode?.trim().toUpperCase() ?? '',
    CountryName: station.CountryName ?? '',
    StationCode: station.StationCode?.trim().toUpperCase() ?? '',
    StationName: station.StationName ?? '',
    AlternativeName: station.AlternativeName ?? '',
    StationType: station.StationType ?? '',
    Tag: raw.Tag ?? '',
    Weightage: Number.isFinite(weightage) ? weightage : 0,
  };
}

export const buildStationsByCode = <TStation>(
  stationsMap?: Record<string, TStation> | null,
  mapStation: (station: TStation) => Station = (station) => station as Station,
): Record<string, Station> =>
  Object.values(stationsMap ?? {}).reduce<Record<string, Station>>(
    (lookup, station) => {
      const snapStation = mapStation(station);
      const stationCode = snapStation.StationCode?.trim().toUpperCase();
      if (stationCode) {
        lookup[stationCode] = {
          ...snapStation,
          StationCode: stationCode,
        };
      }
      return lookup;
    },
    {},
  );

export const trimStationCodeSuffix = (
  displayName: string,
  stationCode?: string,
) =>
  stationCode
    ? displayName.replace(new RegExp(`\\s*\\(${stationCode}\\)$`, 'i'), '')
    : displayName;

export const getStationDisplayName = (
  station?: Pick<Station, 'CityName' | 'StationCode' | 'StationName'> | null,
  fallback = '',
) => {
  const displayName = station
    ? `${station.CityName || station.StationName} (${station.StationCode})`
    : '';

  return trimStationCodeSuffix(displayName, station?.StationCode) || fallback;
};

/**
 * Checks if a station code is a valid SNAP destination from a given origin
 * @param stationCode - Destination station code
 * @param validDestinations - Array of valid destination codes
 */
export function isValidSnapDestination(
  stationCode: string,
  validDestinations: string[],
): boolean {
  return validDestinations.includes(stationCode.toUpperCase());
}

/**
 * Removes duplicate stations by keeping only unique StationCodes
 * Matches production logic from @snap-search-widget/AaHelpers.js:removeDuplicateStations
 * @param stations - Array of stations that may contain duplicates
 * @returns Array of stations with unique StationCodes
 */
export function removeDuplicateStations(stations: Station[]): Station[] {
  const uniqueStationCodes = new Set<string>();
  const uniqueStations: Station[] = [];

  stations.forEach((station) => {
    if (!uniqueStationCodes.has(station.StationCode)) {
      uniqueStationCodes.add(station.StationCode);
      uniqueStations.push(station);
    }
  });

  return uniqueStations;
}
